// src/components/pages/mosque/MosqueDonationOpportunityDetailsPage.tsx

import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";

import { API_BASE_URL } from "../../../config";
import { asErr } from "../../../utils/apiHelpers";

// -------------------------------
// Types (keep simple + tolerant)
// -------------------------------
type CatItem = { id: number; name: string };

type MosqueOpportunityDetail = {
  id: number;
  mosqueId?: number;

  mosqueName?: string;
  fullAddress?: string;

  regionName?: string;
  cityName?: string;
  centerName?: string;
  classificationName?: string;

  hasWomenPrayerRoom?: boolean | null;

  latitude?: number | null;
  longitude?: number | null;

  catA?: CatItem[] | string | null;
  catB?: CatItem[] | string | null;
  catC?: CatItem[] | string | null;
  catD?: CatItem[] | string | null;
  catE?: CatItem[] | string | null;

  createdAt?: string | null;
  updatedAt?: string | null;
};

// -------------------------------
// Helpers
// -------------------------------
const getBaseURL = () => {
  // Use relative URLs in development (proxy) or absolute in production
  if (process.env.NODE_ENV === "development") return "";
  return API_BASE_URL;
};

function toCommaText(v: any): string {
  if (!v) return "";
  if (typeof v === "string") return v.trim();
  if (Array.isArray(v)) return v.map((x) => x?.name).filter(Boolean).join(", ");
  return "";
}

function toBoolText(v: boolean | null | undefined) {
  if (v === true) return "نعم";
  if (v === false) return "لا";
  return "غير متوفر";
}

function buildGoogleMapsLink(lat?: number | null, lng?: number | null) {
  if (lat == null || lng == null) return "";
  return `https://www.google.com/maps?q=${lat},${lng}`;
}

function buildGoogleMapsEmbed(lat?: number | null, lng?: number | null) {
  if (lat == null || lng == null) return "";
  return `https://www.google.com/maps?q=${lat},${lng}&z=15&output=embed`;
}

/**
 * ✅ Decide if current details are for:
 * - "ddr" = Maintenance opportunity
 * - "hdr" = Hygiene opportunity
 *
 * Priority:
 *  1) location.state.kind (when navigating from list)
 *  2) URL querystring ?kind=hdr|ddr (survives refresh/share)
 *  3) Try to auto-detect from passed state.item.request_name (HDR-... → hdr)
 *  4) Default fallback = ddr
 */
function resolveKind(location: ReturnType<typeof useLocation>): "ddr" | "hdr" {
  const state: any = location.state;

  const stateKind = state?.kind;
  if (stateKind === "hdr" || stateKind === "ddr") return stateKind;

  const qsKind = new URLSearchParams(location.search).get("kind")?.toLowerCase();
  if (qsKind === "hdr" || qsKind === "ddr") return qsKind as "hdr" | "ddr";

  const reqName = (state?.item?.request_name ?? state?.item?.requestName ?? "") as string;
  if (typeof reqName === "string" && reqName.toUpperCase().startsWith("HDR-")) return "hdr";

  return "ddr";
}

// -------------------------------
// Data fetch (DDR/HDR aware)
// -------------------------------
async function fetchOpportunityById(id: string, kind: "ddr" | "hdr"): Promise<MosqueOpportunityDetail> {
  const http = axios.create({
    baseURL: getBaseURL(),
    headers: { Accept: "application/json" },
    withCredentials: false,
  });

  /**
   * DDR (Maintenance) details:
   *   GET /api/donations/mosques/opportunities/{id}
   *
   * HDR (Hygiene) details:
   *   GET /api/donations/hygiene/ddr/{id}/json
   */
  const url =
    kind === "hdr"
      ? `/api/donations/hygiene/ddr/${encodeURIComponent(id)}/json`
      : `/api/donations/mosques/opportunities/${encodeURIComponent(id)}`;

  const res = await http.get(url, { validateStatus: (s) => s >= 200 && s < 500 });
  if (res.status >= 400) {
    const msg =
      (res.data && (res.data.message || res.data.error)) ||
      `Request failed (${res.status})`;
    throw new Error(msg);
  }

  const payload = res.data?.data ?? res.data;
  return payload as MosqueOpportunityDetail;
}

// -------------------------------
// Page
// -------------------------------
export default function MosqueDonationOpportunityDetailsPage() {
  const nav = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();

  // ✅ DDR/HDR kind for this page
  const kind = useMemo(() => resolveKind(location), [location]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [item, setItem] = useState<MosqueOpportunityDetail | null>(null);

  // Where to go back (list page). Keep your existing behavior.
  const backTo =
    (location.state as any)?.backTo || "/donations/mosques/opportunities";

  useEffect(() => {
    let alive = true;

    async function run() {
      if (!id) {
        setErr("Missing id in URL.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setErr(null);

        /**
         * ✅ Fast path:
         * If we navigated from the list page, we already have "item" in route state.
         * Show it immediately (nice UX), then still fetch from server to be correct on refresh/share.
         */
        const stateItem = (location.state as any)?.item;
        if (stateItem) {
          setItem(stateItem as MosqueOpportunityDetail);
        }

        // ✅ Correct API based on kind (hdr/ddr)
        const data = await fetchOpportunityById(id, kind);
        if (!alive) return;

        setItem(data);
      } catch (e) {
        if (!alive) return;
        setErr(asErr(e, "Failed to load details").message);
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    }

    run();
    return () => {
      alive = false;
    };
  }, [id, kind, location.state]);

  const title = useMemo(() => {
    if (item?.fullAddress) return item.fullAddress;
    if (item?.mosqueName) return item.mosqueName;
    return kind === "hdr" ? "تفاصيل فرصة النظافة" : "تفاصيل فرصة التبرع";
  }, [item, kind]);

  const mapLink = useMemo(
    () => buildGoogleMapsLink(item?.latitude, item?.longitude),
    [item?.latitude, item?.longitude]
  );

  const mapEmbed = useMemo(
    () => buildGoogleMapsEmbed(item?.latitude, item?.longitude),
    [item?.latitude, item?.longitude]
  );

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-16">
      <div className="space-y-6">
        {/* Top bar */}
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm text-slate-500">
              {kind === "hdr" ? "فرص النظافة / التفاصيل" : "فرص التبرع / التفاصيل"}
            </div>
            <h1 className="mt-1 text-2xl md:text-3xl font-bold text-slate-900 truncate">
              {title}
            </h1>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => nav(backTo)}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50"
            >
              رجوع
            </button>

            {mapLink ? (
              <a
                href={mapLink}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
              >
                فتح الخريطة
              </a>
            ) : null}
          </div>
        </div>

        {/* Loading / Error */}
        {loading ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="animate-pulse space-y-4">
              <div className="h-6 w-2/3 rounded bg-slate-100" />
              <div className="h-4 w-1/2 rounded bg-slate-100" />
              <div className="h-44 w-full rounded bg-slate-100" />
            </div>
          </div>
        ) : err ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6">
            <div className="text-rose-900 font-semibold">
              حدث خطأ أثناء تحميل التفاصيل
            </div>
            <div className="mt-2 text-rose-800 text-sm break-words">{err}</div>

            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="rounded-xl bg-rose-700 px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
              >
                إعادة المحاولة
              </button>
              <Link
                to={backTo}
                className="rounded-xl border border-rose-200 bg-white px-4 py-2 text-sm font-semibold text-rose-800 hover:bg-rose-50"
              >
                العودة للقائمة
              </Link>
            </div>
          </div>
        ) : !item ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            لا توجد بيانات لعرضها
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left: Info */}
            <div className="lg:col-span-7 space-y-6">
              {/* Main card */}
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="p-6">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm text-slate-500">اسم المسجد</div>
                      <div className="mt-1 text-lg font-bold text-slate-900">
                        {item.mosqueName || "غير متوفر"}
                      </div>

                      <div className="mt-3 text-sm text-slate-700">
                        <span className="text-slate-500">العنوان:</span>{" "}
                        {item.fullAddress || "غير متوفر"}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700">
                        رقم الفرصة: {item.id}
                      </span>

                      <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700">
                        مصلى نساء: {toBoolText(item.hasWomenPrayerRoom)}
                      </span>
                    </div>
                  </div>

                  <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="rounded-xl border border-slate-200 p-4">
                      <div className="text-xs text-slate-500">المنطقة</div>
                      <div className="mt-1 font-semibold text-slate-900">
                        {item.regionName || "غير متوفر"}
                      </div>
                    </div>

                    <div className="rounded-xl border border-slate-200 p-4">
                      <div className="text-xs text-slate-500">المدينة</div>
                      <div className="mt-1 font-semibold text-slate-900">
                        {item.cityName || "غير متوفر"}
                      </div>
                    </div>

                    <div className="rounded-xl border border-slate-200 p-4">
                      <div className="text-xs text-slate-500">المركز</div>
                      <div className="mt-1 font-semibold text-slate-900">
                        {item.centerName || "غير متوفر"}
                      </div>
                    </div>

                    <div className="rounded-xl border border-slate-200 p-4">
                      <div className="text-xs text-slate-500">التصنيف</div>
                      <div className="mt-1 font-semibold text-slate-900">
                        {item.classificationName || "غير متوفر"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Categories card */}
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="p-6">
                  <h2 className="text-lg font-bold text-slate-900">فئات التبرع</h2>
                  <p className="mt-1 text-sm text-slate-500">
                    قد تكون الفئات متعددة (مفصولة بفواصل).
                  </p>

                  <div className="mt-5 space-y-4">
                    <div className="rounded-xl border border-slate-200 p-4">
                      <div className="text-xs text-slate-500">الفئة A</div>
                      <div className="mt-1 text-sm font-semibold text-slate-900">
                        {toCommaText(item.catA) || "غير متوفر"}
                      </div>
                    </div>

                    <div className="rounded-xl border border-slate-200 p-4">
                      <div className="text-xs text-slate-500">الفئة B</div>
                      <div className="mt-1 text-sm font-semibold text-slate-900">
                        {toCommaText(item.catB) || "غير متوفر"}
                      </div>
                    </div>

                    <div className="rounded-xl border border-slate-200 p-4">
                      <div className="text-xs text-slate-500">الفئة C</div>
                      <div className="mt-1 text-sm font-semibold text-slate-900">
                        {toCommaText(item.catC) || "غير متوفر"}
                      </div>
                    </div>

                    <div className="rounded-xl border border-slate-200 p-4">
                      <div className="text-xs text-slate-500">الفئة D</div>
                      <div className="mt-1 text-sm font-semibold text-slate-900">
                        {toCommaText(item.catD) || "غير متوفر"}
                      </div>
                    </div>

                    <div className="rounded-xl border border-slate-200 p-4">
                      <div className="text-xs text-slate-500">الفئة E</div>
                      <div className="mt-1 text-sm font-semibold text-slate-900">
                        {toCommaText(item.catE) || "غير متوفر"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Map */}
            <div className="lg:col-span-5 space-y-6">
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                <div className="p-6">
                  <h2 className="text-lg font-bold text-slate-900">
                    الموقع على الخريطة
                  </h2>

                  <div className="mt-2 text-sm text-slate-600">
                    <div>
                      <span className="text-slate-500">خط العرض:</span>{" "}
                      {item.latitude ?? "غير متوفر"}
                    </div>
                    <div>
                      <span className="text-slate-500">خط الطول:</span>{" "}
                      {item.longitude ?? "غير متوفر"}
                    </div>
                  </div>

                  <div className="mt-4">
                    {mapEmbed ? (
                      <div className="rounded-2xl overflow-hidden border border-slate-200">
                        <iframe
                          title="map"
                          src={mapEmbed}
                          className="w-full h-[320px]"
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                        />
                      </div>
                    ) : (
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-600">
                        لا يوجد إحداثيات لعرض الخريطة.
                      </div>
                    )}
                  </div>

                  {mapLink ? (
                    <div className="mt-4">
                      <a
                        href={mapLink}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex w-full items-center justify-center rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
                      >
                        فتح في Google Maps
                      </a>
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="p-6">
                  <h3 className="text-base font-bold text-slate-900">إجراءات سريعة</h3>
                  <div className="mt-4 grid grid-cols-1 gap-3">
                    <Link
                      to={backTo}
                      className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-50"
                    >
                      العودة لنتائج البحث
                    </Link>
                    <button
                      type="button"
                      onClick={() => nav("/donations/mosques/opportunities")}
                      className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-50"
                    >
                      بحث جديد
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="text-xs text-slate-500">
          ملاحظة: عرض البيانات يعتمد على ما هو متوفر من النظام.
        </div>
      </div>
    </div>
  );
}